import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  Phone,
  Mail,
  Check,
  Droplets,
  Flame,
  Hammer,
  Building2,
} from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { SERVICE_META, SERVICE_KEYS, CONTACT_INFO } from "../lib/data";
import { buildQuoteMailto } from "../lib/mailto";

const ICONS = { Droplets, Flame, Hammer, Building2 };

export default function ServiceDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();

  if (!SERVICE_KEYS.includes(slug)) {
    return <Navigate to="/dienstleistungen" replace />;
  }

  const meta = SERVICE_META[slug];
  const s = t.services[slug];
  const Icon = ICONS[meta.icon];
  const others = SERVICE_KEYS.filter((k) => k !== slug);

  return (
    <div data-testid={`page-service-${slug}`}>
      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-50">
        <div className="grid lg:grid-cols-2 min-h-[480px]">
          <div className="flex items-center px-4 sm:px-6 lg:px-12 xl:px-20 py-14 lg:py-20">
            <div className="max-w-xl">
              <Link
                to="/dienstleistungen"
                data-testid="service-detail-back"
                className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-[#0022FF] mb-6"
              >
                <ArrowLeft className="h-4 w-4" />
                {t.nav.services}
              </Link>
              <div
                className="inline-flex items-center gap-2 h-14 w-14 rounded-2xl mb-6"
                style={{ backgroundColor: `${meta.color}15`, color: meta.color }}
              >
                <Icon className="h-7 w-7 m-auto" />
              </div>
              <h1 className="font-display font-black text-slate-900 text-5xl sm:text-6xl tracking-tight">
                {s.name}
              </h1>
              <p className="mt-3 text-lg font-medium text-[#0022FF]">{s.short}</p>
              <p className="mt-6 text-slate-600 leading-relaxed">{s.long}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={buildQuoteMailto(lang)}
                  data-testid="service-detail-cta-quote"
                  className="inline-flex items-center gap-2 rounded-full btn-shk-primary px-6 py-3 text-sm font-semibold"
                >
                  <Mail className="h-4 w-4" />
                  {t.services.ctaQuote}
                </a>
                <a
                  href={`tel:${CONTACT_INFO.phoneClean}`}
                  data-testid="service-detail-cta-call"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-800 hover:border-[#0022FF] hover:text-[#0022FF]"
                >
                  <Phone className="h-4 w-4" />
                  {t.services.ctaAppointment}
                </a>
              </div>
            </div>
          </div>
          <div className="relative h-72 lg:h-auto overflow-hidden">
            <img src={meta.image} alt={s.name} className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-slate-50/30" />
          </div>
        </div>
      </section>

      {/* Benefits + Process */}
      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Benefits */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0022FF] mb-3">
                — {t.services.benefitsTitle}
              </div>
              <h2 className="font-display font-black text-slate-900 text-3xl sm:text-4xl tracking-tight mb-8">
                {t.services.benefitsTitle}
              </h2>
              <ul className="space-y-3">
                {s.benefits.map((b, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                    className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 hover:border-[#0022FF] transition-colors"
                  >
                    <div className="h-8 w-8 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                      <Check className="h-4 w-4" strokeWidth={3} />
                    </div>
                    <span className="font-medium text-slate-800">{b}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Process */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0022FF] mb-3">
                — {t.services.processTitle}
              </div>
              <h2 className="font-display font-black text-slate-900 text-3xl sm:text-4xl tracking-tight mb-8">
                {t.services.processTitle}
              </h2>
              <ol className="space-y-4 relative">
                {s.process.map((p, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-start gap-4"
                  >
                    <div className="font-display font-black text-3xl text-[#0022FF]/30 leading-none w-14 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="flex-1 pb-4 border-b border-slate-200 last:border-0">
                      <div className="font-display font-bold text-lg text-slate-900">{p}</div>
                    </div>
                  </motion.li>
                ))}
              </ol>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Other services */}
      <section className="py-16 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="font-display font-black text-2xl sm:text-3xl text-slate-900 tracking-tight mb-6">
            {t.services.allServicesTitle}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {others.map((k) => {
              const m = SERVICE_META[k];
              const OIcon = ICONS[m.icon];
              return (
                <Link
                  key={k}
                  to={`/dienstleistungen/${k}`}
                  data-testid={`service-other-${k}`}
                  className="group flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#0022FF] hover:shadow-md transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="h-10 w-10 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${m.color}15`, color: m.color }}
                    >
                      <OIcon className="h-5 w-5" />
                    </div>
                    <span className="font-display font-bold text-slate-900">{t.services[k].name}</span>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-[#0022FF] group-hover:translate-x-1 transition-all" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
